import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MapPin,
  Clock,
  Users,
  Camera,
  Calendar,
  Euro,
  Settings,
  TreePine,
  Home,
  CheckCircle,
  Star,
  ArrowRight,
} from "lucide-react";
import { useBookingModal } from "../../contexts/BookingModalContext";
import { useTranslation } from "react-i18next";

export const ZaanseSchansPage: React.FC = () => {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();
  const tourHighlights = [
    {
      icon: Settings,
      title: t("1689", "Historic Windmills"),
      description: t(
        "1690",
        "Explore iconic working windmills and traditional crafts"
      ),
    },
    {
      icon: Home,
      title: t("1691", "Green Houses"),
      description: t(
        "1692",
        "Visit authentic 18th & 19th century wooden houses"
      ),
    },
    {
      icon: TreePine,
      title: t("1693", "Traditional Crafts"),
      description: t(
        "1694",
        "Watch clog making and cheese production demonstrations"
      ),
    },
    {
      icon: Camera,
      title: t("1482", "Photography"),
      description: t(
        "1695",
        "Perfect opportunities for stunning countryside photos"
      ),
    },
  ];

  const tourFeatures = [
    t("1484", "Transportation service available"),
    t("1696", "Windmill tours and demonstrations"),
    t("1697", "Traditional craft workshops"),
    t("1698", "Museum visits included"),
    t("1699", "Cheese tasting opportunities"),
    t("1489", "Photography opportunities"),
  ];

  const tourDetails = [
    {
      label: t("1490", "Duration"),
      value: t("1700", "Full Day (6-8 hours)"),
      icon: Clock,
    },
    {
      label: t("1492", "Group Size"),
      value: t("1493", "2-8 People"),
      icon: Users,
    },
    {
      label: t("1494", "Starting Point"),
      value: t("1495", "Schiphol Airport"),
      icon: MapPin,
    },
    {
      label: t("1496", "Price From"),
      value: t("1701", "€120 per person"),
      icon: Euro,
    },
  ];

  return (
    <div className="pt-20 bg-gray-900 min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/tours/Zaanse Schans.webp"
            alt={t("1702", "Zaanse Schans - Historic Windmills")}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-linear-to-r from-gray-900/90 to-gray-800/90" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 bg-primary/20 text-primary px-6 py-3 rounded-full text-sm font-medium mb-6"
            >
              <Settings className="h-4 w-4" />
              <span>{t("1499", "Premium Tour Experience")}</span>
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-bold mb-8">
              {t("1500", "Discover")}{" "}
              <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                {t("1703", "Zaanse Schans")}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed mb-8">
              {t(
                "1704",
                "Historic windmills and traditional Dutch craftsmanship in a picturesque countryside setting"
              )}
            </p>

            <div className="flex items-center justify-center space-x-1 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 text-warning fill-current" />
              ))}
              <span className="text-white ml-2 text-lg">
                {t("1705", "4.9/5 (127 reviews)")}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tour Overview */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                  {t("1706", "Experience Historic")}{" "}
                  <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                    {t("1703", "Zaanse Schans")}
                  </span>
                </h2>

                <p className="text-lg text-gray-100 leading-relaxed">
                  {t(
                    "1707",
                    "Step back in time at Zaanse Schans, where traditional Dutch culture comes alive through historic windmills and authentic 18th-century craftsmanship."
                  )}
                </p>

                <p className="text-gray-200 leading-relaxed">
                  {t(
                    "1708",
                    "This unique open-air museum showcases the Netherlands' industrial heritage with working windmills, traditional green wooden houses, and artisan workshops where you can witness clog making, cheese production, and other time-honored crafts."
                  )}
                </p>
              </div>

              {/* Tour Highlights */}
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">
                  {t("1515", "Tour Highlights")}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {tourHighlights.map((highlight) => (
                    <motion.div
                      key={highlight.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                      className="bg-gray-800/50 backdrop-blur-xs p-4 rounded-xl border border-gray-700"
                    >
                      <highlight.icon className="h-8 w-8 text-primary mb-3" />
                      <div className="font-semibold text-white text-sm mb-2">
                        {highlight.title}
                      </div>
                      <p className="text-xs text-gray-300 leading-relaxed">
                        {highlight.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4 h-[600px]">
                <div className="space-y-4">
                  <div className="relative overflow-hidden rounded-2xl shadow-xl">
                    <img
                      src="/images/tours/Zaanse Schans.webp"
                      alt={t("1709", "Zaanse Schans Windmills")}
                      className="w-full h-60 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="relative overflow-hidden rounded-2xl shadow-xl mt-8">
                    <img
                      src="/images/tours/Zaanse Schans.webp"
                      alt={t("1597", "Traditional Houses")}
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </div>
                <div className="relative overflow-hidden rounded-2xl shadow-xl col-span-2">
                  <img
                    src="/images/tours/Zaanse Schans.webp"
                    alt={t("1710", "Craft Workshops")}
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tour Details & Booking */}
      <section className="py-24 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t("1512", "Tour Details & Information")}
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              {t(
                "1711",
                "Everything you need to know about your Zaanse Schans experience"
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Tour Details */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl font-bold text-white mb-8">
                {t("1514", "Tour Details")}
              </h3>

              <div className="space-y-6">
                {tourDetails.map((detail) => (
                  <motion.div
                    key={detail.label}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex items-center space-x-4 p-4 bg-gray-700/30 rounded-xl"
                  >
                    <div className="bg-primary/20 p-3 rounded-lg">
                      <detail.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">
                        {detail.label}
                      </div>
                      <div className="text-gray-300">{detail.value}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Tour Highlights */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl font-bold text-white mb-8">
                {t("1712", "What's Included")}
              </h3>

              <div className="space-y-4">
                {tourFeatures.map((item) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex items-start space-x-3"
                  >
                    <CheckCircle className="h-5 w-5 text-primary mt-1 shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-linear-to-r from-primary via-primary-light to-primary rounded-3xl p-12 text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.1\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'2\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] bg-repeat"></div>

            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("1713", "Ready to Explore Zaanse Schans?")}
              </h2>
              <p className="text-xl mb-8 opacity-90">
                {t("1714", "Book your historic windmill adventure today")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={openModal}
                  className="bg-white text-primary px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center"
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  {t("1518", "Book Now")}
                </button>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="border-2 border-white text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white hover:text-primary transition-all duration-300 flex items-center space-x-2"
                  >
                    <span>{t("1519", "Ask Questions")}</span>
                    <ArrowRight className="h-5 w-5" />
                  </motion.button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
